import {
    ACTIVITY_PARAM,
    END_PARAM,
    PERIOD_PARAM,
    ROW_PARAM,
    START_PARAM,
    STATS_LINK_PARAMS,
    STATS_PERIOD_PARAMS,
    STATS_VIEW,
    TOPIC_PARAM,
    VIEW_PARAM,
    BACK_PARAM,
    INTENT_PARAM,
    CUSTOM_PERIOD,
    GUIDES_VIEW,
    GUIDE_PARAM,
    QUERY_PARAM,
    GUIDES_LINK_PARAMS,
    linkTarget,
    statsPeriod,
} from "./insights-links";

/**
 * The Insights page's two views, the activities and Stats (D42 of
 * documentation/pr-plans/insights-s0-plan-and-seats.md), mounted on resources/views/sites/analytics.blade.php,
 * and the browser history the activity menu moves through (D96 of
 * documentation/pr-plans/insights-simplify-plan.md, P7).
 *
 * The activity menu lists both views (ActionsView draws it): each item is a link into this page, and one
 * press handler here takes every link into the page (iv_click()), so an item, an activity's "See this topic
 * in Stats" and Stats' key figures all open in place, with no page load (D76), whichever view, activity or
 * menu drew the link. A press with a modifier key, or any other link, keeps the browser's own meaning.
 *
 * Each such press adds one browser history entry, for the address it opened: an activity (?activity=, and
 * &row= when it names one), or Stats (?view=stats, with the topic a link focused). Back and Forward move
 * between those entries in place (iv_popped()): Stats or the activity shows again, each pane as it was left,
 * and back on an activity left for Stats, focus returns to the link the reader left from (D111, B7). The
 * activities' own fields (?activity=, &row=) are Livewire's, which replaces them as they change, so one press
 * that changes both the view and the activity still adds one entry.
 *
 * A See in Stats link from an activity opens Stats over that activity's Period (D111, B7): written into the
 * address as Stats' own ?period=, ?start= and ?end=, which a reload opens on, and set on Stats as its own
 * Period control sets it, at once if it has mounted, else as soon as its lazy load lands (iv_applyPeriod()).
 */
export const VIEWS = ["actions", "stats", "guides"];

/** Sent on window each time a view shows, {view}: the topics card ends a link's focus once Stats is left. */
export const VIEW_SHOWN_EVENT = "insights-view-shown";

/**
 * Sent on window when a link opens the Guides view (D181), {guide, section, query}: the guides script shows the
 * hub, the search results or the guide, at the section the link names (insights/guides.js).
 */
export const OPEN_GUIDE_EVENT = "insights-open-guide";

/** Sent on window when a link opens Stats on a topic, {topic}: the topics card focuses it (D76). */
export const FOCUS_TOPIC_EVENT = "insights-focus-topic";

/**
 * Opens an activity in place, {activity, origin, row, returnTo}: a menu item, a key figure, Back or a link
 * sends it, this script shows the activities, and the Actions view opens the activity (insights-actions.js).
 */
export const OPEN_ACTIVITY_EVENT = "insights-open-activity";

// Stats' Livewire component (Sites\Analytics), whose Period a See in Stats link sets once it has mounted.
const STATS_COMPONENT = "sites.analytics";

// The activities' Livewire components, whose window control's time_range is the list's Period.
const ACTIVITY_COMPONENTS = "sites.insights.activities.";

/**
 * @param {object}   config
 * @param {string}   config.view                The view the server rendered (InsightsView).
 * @param {string}   config.topicsChangedEvent  TopicEditor::CHANGED_EVENT, which Stats' own view hears.
 * @param {string}   config.brandTermsChangedEvent  Analytics::BRAND_TERMS_CHANGED_EVENT, which Stats' own view hears.
 * @param {string[]} config.activities          The six activities' keys (InsightsLinks::activityKeys()).
 * @param {Record<string, {period: string, start?: string, end?: string}>} config.periods  InsightsLinks::statsPeriods().
 */
export default function InsightsViews({ view, topicsChangedEvent, brandTermsChangedEvent, activities = [], periods = {} }) {
    const initial = VIEWS.includes(view) ? view : "actions";
    const activityOf = (key) => (activities.includes(key) ? key : null);
    const address = () => new URL(window.location.href);

    return {
        view: initial,

        // Whether Stats has been shown on this page, so that it has figures a change on Actions can
        // leave stale: Stats that never loaded reads everything as it loads.
        iv_statsSeen: initial === "stats",

        // The site whose topics an activity changed while Stats was hidden, until Stats is shown.
        iv_topicsChangedSiteId: null,

        // The site whose brand terms changed while Stats was hidden, until Stats is shown.
        iv_brandTermsChangedSiteId: null,

        /**
         * Always null: the browser's Back returns to the activity (D111), and Stats' Back slot, which showed
         * while this was not, is gone (P8). Kept while InsightsInPlaceLinksTest still reads it.
         */
        iv_back: null,

        /** The link in the activity the reader left for Stats, where Back returns focus; null after a reload. */
        iv_returnTo: null,

        // The popstate listener, removed with the component.
        iv_onPop: null,

        // A Period a link asked of Stats before Stats had mounted, and the Livewire hook that waits for it to.
        iv_pendingPeriod: null,
        iv_unhookStats: null,

        init() {
            // The address names the view the page opened on, and nothing else: ?view=actions, or a
            // view that does not exist, reads as the activities do, with no parameter.
            this.iv_writeAddress();

            this.$watch("view", (next) => this.iv_shown(next));

            this.iv_onPop = (event) => this.iv_popped(event);
            window.addEventListener("popstate", this.iv_onPop);
        },

        destroy() {
            window.removeEventListener("popstate", this.iv_onPop);
            this.iv_unhookStats?.();
        },

        /** A view shown from the page: a menu item, a key figure, Back or an in-place link. */
        iv_show(next) {
            if (VIEWS.includes(next)) {
                this.view = next;
            }
        },

        /** A view was shown: the address names it, Stats catches up, and the topics card hears which view shows. */
        iv_shown(next) {
            if (!VIEWS.includes(next)) {
                this.view = "actions";

                return;
            }

            if (next === "stats") {
                this.iv_statsSeen = true;
                this.iv_tellStats();
            }

            this.iv_writeAddress();
            window.dispatchEvent(new CustomEvent(VIEW_SHOWN_EVENT, { detail: { view: next } }));
        },

        /**
         * The address for the view on screen, replaced: Stats names itself; the activities drop Stats' own
         * link parameters. Stats' filters (its Period among them) are Stats' and stay. The activity open
         * (?activity=, &row=) is the Actions view's, and stays, so a reload keeps it open behind Stats.
         */
        iv_writeAddress() {
            const url = address();

            if (this.view === "stats") {
                url.searchParams.set(VIEW_PARAM, STATS_VIEW);
                GUIDES_LINK_PARAMS.forEach((key) => url.searchParams.delete(key));
            } else if (this.view === "guides") {
                // The guide and the search are the guides script's own (insights/guides.js), which writes them.
                url.searchParams.set(VIEW_PARAM, GUIDES_VIEW);
                STATS_LINK_PARAMS.forEach((key) => url.searchParams.delete(key));
            } else {
                url.searchParams.delete(VIEW_PARAM);
                STATS_LINK_PARAMS.forEach((key) => url.searchParams.delete(key));
                GUIDES_LINK_PARAMS.forEach((key) => url.searchParams.delete(key));
                url.hash = "";
            }

            if (url.href !== window.location.href) {
                window.history.replaceState(window.history.state, "", url);
            }
        },

        /**
         * A new browser history entry for an address this page opens in place (D96): Back returns to the
         * one before. The state carried over is Livewire's, which keeps its fields' values in it.
         */
        iv_push(url) {
            if (url.href !== window.location.href) {
                window.history.pushState(window.history.state, "", url);
            }
        },

        /** The activity open on Actions, as the address names it (ActionsView's ?activity=), or null. */
        iv_activityBehind() {
            return activityOf(address().searchParams.get(ACTIVITY_PARAM));
        },

        /**
         * A press on a link into this page (D76, D96): a menu item, an activity's link to a topic on Stats, or a
         * link to an activity's row, opened in place rather than loaded, as one new history entry. Left to the
         * browser: a press another handler has taken, any button but the main one, a modifier key (a new
         * tab, a new window), a link to another target, and any link that does not name a view, topic or
         * activity of this page.
         */
        iv_click(event) {
            if (event.defaultPrevented || event.button !== 0 || event.metaKey || event.ctrlKey || event.shiftKey || event.altKey) {
                return;
            }

            const link = event.target?.closest?.("a[href]");

            if (!link || (link.target && link.target !== "_self") || link.hasAttribute("download")) {
                return;
            }

            // An anchor into this page ("#main-content") is the browser's, as on any page: every link into a view
            // carries its address (?view=, ?activity=), while a fragment alone resolves against the page's own,
            // activity and all, and would read as a press on the open activity.
            if (link.getAttribute?.("href")?.startsWith("#")) {
                return;
            }

            const target = linkTarget(new URL(link.href, window.location.href), address(), activities);

            if (target === null) {
                return;
            }

            event.preventDefault();

            if (target.view === STATS_VIEW) {
                this.iv_toStats(target.topic, link);

                return;
            }

            if (target.view === GUIDES_VIEW) {
                this.iv_toGuides(target, link);

                return;
            }

            const url = address();

            url.searchParams.delete(VIEW_PARAM);
            STATS_LINK_PARAMS.forEach((key) => url.searchParams.delete(key));
            GUIDES_LINK_PARAMS.forEach((key) => url.searchParams.delete(key));
            url.hash = "";
            url.searchParams.set(ACTIVITY_PARAM, target.activity);

            if (target.row !== null) {
                url.searchParams.set(ROW_PARAM, target.row);
            } else {
                url.searchParams.delete(ROW_PARAM);
            }

            this.iv_push(url);
            window.dispatchEvent(new CustomEvent(OPEN_ACTIVITY_EVENT, { detail: { activity: target.activity, row: target.row, origin: link } }));
        },

        /**
         * Stats, focused on a topic when a link names one: switched to in place, as a new history entry, or
         * focused again if Stats already shows. A link from an activity opens Stats over that activity's
         * Period (iv_applyPeriod()), and focus, which was on a link now hidden, moves to Stats' pane, from
         * where the topics card takes it to the topic's row; the link is kept for Back. The page's top comes
         * back into view at once, so Stats is not shown from the middle of where the activity was scrolled to;
         * the card then brings the topic on screen.
         */
        iv_toStats(topic, origin = null) {
            const period = this.iv_periodOf(origin);
            const url = address();

            url.searchParams.set(VIEW_PARAM, STATS_VIEW);
            url.searchParams.delete(BACK_PARAM);
            // A link names a topic, which opens in the band its own search intent puts it in (D148), never in
            // the band the card was left open on.
            url.searchParams.delete(INTENT_PARAM);

            if (topic !== null) {
                url.searchParams.set(TOPIC_PARAM, String(topic));
            } else {
                url.searchParams.delete(TOPIC_PARAM);
            }

            if (period !== null) {
                STATS_PERIOD_PARAMS.forEach((key) => url.searchParams.delete(key));
                Object.entries(period).forEach(([key, value]) => url.searchParams.set(key, value));
            }

            this.iv_push(url);

            if (period !== null) {
                this.iv_applyPeriod(period);
            }

            if (this.view !== "stats") {
                this.iv_returnTo = this.iv_origin(origin);
                this.iv_show("stats");
                this.$root.scrollIntoView?.({ block: "start" });
                this.$nextTick(() => document.getElementById("insights-view-stats")?.focus({ preventScroll: true }));
            }

            if (topic !== null) {
                window.dispatchEvent(new CustomEvent(FOCUS_TOPIC_EVENT, { detail: { topic } }));
            }
        },

        /**
         * The Guides view (D181), on its hub, its search results or one guide at a section: switched to in place,
         * as one new history entry whose address a reload opens on. A link from an activity keeps that link for
         * Back (iv_returnTo), as a See in Stats link does; the page's top comes into view, and focus moves to the
         * guides' pane, from where the guides script takes it to the section.
         */
        iv_toGuides(target, origin = null) {
            const url = address();

            url.searchParams.set(VIEW_PARAM, GUIDES_VIEW);
            STATS_LINK_PARAMS.forEach((key) => url.searchParams.delete(key));

            if (target.guide !== null) {
                url.searchParams.set(GUIDE_PARAM, target.guide);
            } else {
                url.searchParams.delete(GUIDE_PARAM);
            }

            if (target.query !== null) {
                url.searchParams.set(QUERY_PARAM, target.query);
            } else {
                url.searchParams.delete(QUERY_PARAM);
            }

            url.hash = target.section !== null ? `#${target.section}` : "";

            this.iv_push(url);

            if (this.view !== "guides") {
                this.iv_returnTo = this.iv_origin(origin);
                this.iv_show("guides");
            }

            // For every link into the guides, not only the first: a guide opened from the foot of the hub, or the
            // hub from a guide's crumb, would otherwise show from where the last page was scrolled to, its title
            // under the bar, and leave focus on a link now hidden. Back and Forward (iv_popped) scroll nothing:
            // the browser puts the reader back where they were.
            this.$root.scrollIntoView?.({ block: "start" });
            this.$nextTick(() => document.getElementById("insights-view-guides")?.focus({ preventScroll: true }));

            window.dispatchEvent(new CustomEvent(OPEN_GUIDE_EVENT, { detail: { guide: target.guide, section: target.section, query: target.query } }));
        },

        /**
         * Back or Forward moved to another entry of this page (D96): the view and the activity it names show
         * again, in place. Back from Stats or Guides to the activity it was opened from puts focus on the link the
         * reader left from (B7). An entry the page did not write (another page's, or a hash) changes nothing.
         */
        iv_popped(event) {
            const url = address();
            const view = url.searchParams.get(VIEW_PARAM);
            const next = view === STATS_VIEW ? "stats" : view === GUIDES_VIEW ? "guides" : "actions";

            if (next === "guides") {
                this.iv_show("guides");
                // Focus moves onto the pane, never scrolling (the browser restores the reader's place), off whatever
                // the entry left is hiding, so the next Tab continues from the guides; a section named takes it on.
                this.$nextTick(() => document.getElementById("insights-view-guides")?.focus({ preventScroll: true }));
                window.dispatchEvent(new CustomEvent(OPEN_GUIDE_EVENT, { detail: { guide: url.searchParams.get(GUIDE_PARAM) || null, section: url.hash.replace(/^#/, "") || null, query: url.searchParams.get(QUERY_PARAM) || null } }));

                return;
            }

            if (next === "stats") {
                this.iv_show("stats");

                const topic = url.searchParams.get(TOPIC_PARAM);

                if (topic !== null) {
                    window.dispatchEvent(new CustomEvent(FOCUS_TOPIC_EVENT, { detail: { topic: Number(topic) } }));
                }

                return;
            }

            // An entry with no ?activity= is the one the page opened on, whose activity Livewire kept in its state.
            const activity = activityOf(url.searchParams.get(ACTIVITY_PARAM)) ?? activityOf(event?.state?.alpine?.[ACTIVITY_PARAM]?.value);
            const returnTo = this.view !== "actions" ? this.iv_returnTo : null;

            this.iv_returnTo = null;

            if (activity !== null) {
                window.dispatchEvent(new CustomEvent(OPEN_ACTIVITY_EVENT, { detail: { activity, row: url.searchParams.get(ROW_PARAM) || null, returnTo } }));
            }

            this.iv_show("actions");
        },

        /** An activity opened in place (insights-open-activity): the activities show, where the Actions view opens it. */
        iv_openActivity() {
            this.iv_show("actions");
        },

        /**
         * Where Back returns focus for a link pressed into Stats or the guides (B7): the link, where it sits in an
         * activity's pane; the button of the menu that holds it (a chip's See in Stats on Sort my searches' board, P72:
         * the menu closed with the press, so the link is hidden), where that button sits in a pane; the trigger of the
         * tooltip whose panel holds it (the panel is drawn in <body>, x-teleport, and the link in it is hidden once
         * the tooltip closes), where that trigger sits in a pane; else nothing, and the activity's open row or heading
         * takes it.
         */
        iv_origin(link) {
            if (!link) {
                return null;
            }

            const menu = link.closest?.('[role="menu"]');

            if (menu) {
                const menuId = menu.id ? (typeof CSS !== "undefined" && CSS.escape ? CSS.escape(menu.id) : menu.id) : null;
                const button = menuId !== null ? document.querySelector(`[aria-controls="${menuId}"]`) : null;

                return button?.closest?.("[data-insights-activity-pane]") ? button : null;
            }

            if (link.closest?.("[data-insights-activity-pane]")) {
                return link;
            }

            const panel = link.closest?.("[data-insights-tooltip-panel]");
            const id = panel?.id ? (typeof CSS !== "undefined" && CSS.escape ? CSS.escape(panel.id) : panel.id) : null;
            const trigger = id !== null ? document.querySelector(`[aria-controls="${id}"]`) : null;

            return trigger?.closest?.("[data-insights-activity-pane]") ? trigger : null;
        },

        /**
         * Stats' Period for a link pressed in an activity (D111): the period that activity's list is read over,
         * its window control's time_range, as Stats names it; null for a link from anywhere else, or from an
         * activity with no Period (Sort my searches, D106), which leaves Stats on its own.
         */
        iv_periodOf(link) {
            const pane = link?.closest?.("[data-insights-activity-pane]");

            if (!pane) {
                return null;
            }

            const component = (window.Livewire?.all?.() ?? []).find((each) => String(each.name).startsWith(ACTIVITY_COMPONENTS) && pane.contains(each.el));

            return statsPeriod(component?.$wire?.timeRange, periods);
        },

        /**
         * Stats over `period`, set as its own Period control sets it (its time_range), so it reads again over
         * those days. Stats mounts lazily, and Livewire seeds a lazy component's #[Url] properties from the page's
         * first address, never from the one its lazy load is sent from, so a Stats not mounted yet is told as
         * soon as its lazy load lands (one commit hook, gone once it has); the address iv_toStats() wrote is
         * what a reload opens on.
         */
        iv_applyPeriod(period) {
            const stats = (window.Livewire?.all?.() ?? []).find((each) => each.name === STATS_COMPONENT);
            const current = stats?.$wire?.timeRange;

            if (typeof current !== "string") {
                this.iv_pendingPeriod = period;
                this.iv_unhookStats ??= window.Livewire?.hook?.("commit", ({ component, succeed }) => {
                    if (component.name !== STATS_COMPONENT) {
                        return;
                    }

                    // A task later, once the answer has given Stats its properties.
                    succeed(() => setTimeout(() => this.iv_statsMounted(), 0));
                }) ?? null;

                return;
            }

            const timeRange = statsTimeRange(period);

            if (timeRange !== null && timeRange !== current) {
                stats.$wire.set("timeRange", timeRange);
            }
        },

        /** Stats answered a request: the Period a link asked of it before it mounted, applied once it has. */
        iv_statsMounted() {
            const stats = (window.Livewire?.all?.() ?? []).find((each) => each.name === STATS_COMPONENT);

            if (this.iv_pendingPeriod === null || typeof stats?.$wire?.timeRange !== "string") {
                return;
            }

            const period = this.iv_pendingPeriod;

            this.iv_pendingPeriod = null;
            this.iv_unhookStats?.();
            this.iv_unhookStats = null;
            this.iv_applyPeriod(period);
        },

        /**
         * The site's brand terms changed (insights-brand-terms-saved), from Sort my searches (S43). Every
         * figure in Stats is counted with them, so once it has loaded it reads again, when it is next shown,
         * as after a topic edit; it is never reloaded with the page. The opened activities are the Actions
         * view's to read again (ActionsView::brandTermsSaved()).
         */
        iv_brandTermsSaved(detail) {
            if (!this.iv_statsSeen) {
                return;
            }

            this.iv_brandTermsChangedSiteId = detail?.siteId ?? null;

            if (this.view === "stats") {
                this.iv_tellStats();
            }
        },

        /**
         * An activity changed the site's topics (insights-topics-changed). Stats groups its figures by
         * them, so once it has loaded it reads its topic cards again, but only when it is next shown:
         * sorting a page of searches would otherwise spend the cards' rate limit (D31) on figures
         * hidden behind the activity. Stats that never loaded reads everything as it loads.
         */
        iv_topicsChanged(detail) {
            if (!this.iv_statsSeen) {
                return;
            }

            this.iv_topicsChangedSiteId = detail?.siteId ?? null;

            if (this.view === "stats") {
                this.iv_tellStats();
            }
        },

        // On the window, where Stats' own view listens (livewire/sites/analytics.blade.php), since
        // Stats takes no #[On] listener. One event at most: a brand-term change reads everything
        // again, which takes in what a topic edit reads (Sites\Analytics::refreshAfterBrandTermsChanged()).
        iv_tellStats() {
            const brandTermsSiteId = this.iv_brandTermsChangedSiteId;
            const topicsSiteId = this.iv_topicsChangedSiteId;

            this.iv_brandTermsChangedSiteId = null;
            this.iv_topicsChangedSiteId = null;

            if (brandTermsSiteId !== null) {
                window.dispatchEvent(new CustomEvent(brandTermsChangedEvent, { detail: { siteId: brandTermsSiteId } }));
            } else if (topicsSiteId !== null) {
                window.dispatchEvent(new CustomEvent(topicsChangedEvent, { detail: { siteId: topicsSiteId } }));
            }
        },
    };
}

/**
 * A Stats Period as Stats' own Period control writes it (Sites\Analytics::$timeRange): the preset's value
 * for one of its presets, or a range with its end excluded; null for anything else.
 *
 * @param {{period: string, start?: string, end?: string}|null} period
 */
export function statsTimeRange(period) {
    if (!period || typeof period[PERIOD_PARAM] !== "string") {
        return null;
    }

    if (period[PERIOD_PARAM] !== CUSTOM_PERIOD) {
        return period[PERIOD_PARAM];
    }

    const start = period[START_PARAM];
    const end = period[END_PARAM];

    if (typeof start !== "string" || typeof end !== "string") {
        return null;
    }

    const after = new Date(`${end}T00:00:00Z`);

    after.setUTCDate(after.getUTCDate() + 1);

    return `${start} : ${after.toISOString().slice(0, 10)}`;
}
